import React from 'react';
import { Box, ThemeProvider } from '@mui/material';
import useMarketingFonts from '../marketing/useMarketingFonts';
import useSeoHead from '../marketing/useSeoHead';
import MarketingNav from '../marketing/components/MarketingNav';
import MarketingFooter from '../marketing/components/MarketingFooter';
import { PillarHero, PillarFeatureRow, PillarFeatureGrid, PillarClosingCta } from '../marketing/components/PillarPage';
import { marketingColors as C, marketingFonts as F } from '../theme_landing_page';
import { homepageStyles, homepageTheme } from '../theme_landing_page_2';
import Eyebrow from '../marketing/components/Eyebrow';

const features = [
  { title: 'AI certificate extraction', body: 'Upload a PDF and MillOS reads it, tags it and files it against the right property.' },
  { title: 'Cascading reminders', body: 'Email, SMS and calendar alerts to landlord, tenant and contractor, with escalation.' },
  { title: 'Engineer directory', body: 'Gas Safe, NICEIC and DEA engineers surfaced by postcode when a renewal is due.' },
  { title: 'Fire & HMO workflows', body: 'Fire Risk Assessments, emergency lighting, PAT and HMO licences tracked in full.' },
  { title: 'Audit-ready evidence log', body: 'Every action time-stamped and exportable as one evidence PDF per property.' },
  { title: 'Multi-user roles', body: 'Scoped access for agents, contractors and accountants across the portfolio.' },
];

const VaultPanel = (
  <>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: '14px' }}>
      <Box>
        <Box sx={{ fontFamily: F.display, fontSize: 30, lineHeight: 1, color: C.ink }}>£32,400</Box>
        <Box sx={{ fontSize: 11, color: C.bodySoft }}>in fines avoided / year</Box>
      </Box>
      <Box sx={{ textAlign: 'right' }}>
        <Box sx={{ fontFamily: F.display, fontSize: 30, lineHeight: 1, color: C.accent }}>6/6</Box>
        <Box sx={{ fontSize: 11, color: C.bodySoft }}>certs valid · 12 Roseberry</Box>
      </Box>
    </Box>
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '9px' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: C.panelSoft, border: `1px solid ${C.cardHairline}`, borderRadius: '11px', padding: '12px 14px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '9px', fontSize: 14, color: C.ink }}>
          <Box sx={{ width: 26, height: 26, borderRadius: '7px', background: C.tintSuccess, display: 'grid', placeItems: 'center', color: C.success, fontSize: 12 }}>✓</Box>
          EPC · Band C
        </Box>
        <Box sx={{ fontSize: 12, color: C.success, fontWeight: 600 }}>Valid · 6 yrs</Box>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: C.panelSoft, border: `1px solid ${C.cardHairline}`, borderRadius: '11px', padding: '12px 14px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '9px', fontSize: 14, color: C.ink }}>
          <Box sx={{ width: 26, height: 26, borderRadius: '7px', background: C.tintWarning, display: 'grid', placeItems: 'center', color: C.warning, fontSize: 12 }}>⏱</Box>
          Gas Safety · CP12
        </Box>
        <Box sx={{ fontSize: 12, color: C.warning, fontWeight: 600 }}>42 days</Box>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: C.accent, borderRadius: '11px', padding: '12px 14px' }}>
        <Box sx={{ fontSize: 14, color: C.white }}>EICR · 5-year</Box>
        <Box sx={{ fontSize: 11.5, color: C.white, background: 'rgba(255,255,255,0.22)', padding: '3px 10px', borderRadius: '20px', fontWeight: 600 }}>AI renewing</Box>
      </Box>
    </Box>
  </>
);

const ScorePanel = (
  <>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '18px' }}>
      <Box sx={{ position: 'relative', width: 92, height: 92, flex: 'none', display: 'grid', placeItems: 'center' }}>
        <svg width="92" height="92" viewBox="0 0 36 36">
          <circle cx="18" cy="18" r="15.5" stroke={C.border} strokeWidth={3.5} fill="none" />
          <circle cx="18" cy="18" r="15.5" stroke={C.accent} strokeWidth={3.5} fill="none" strokeLinecap="round" strokeDasharray="97.4" strokeDashoffset="6" transform="rotate(-90 18 18)" />
        </svg>
        <Box sx={{ position: 'absolute', textAlign: 'center' }}>
          <Box sx={{ fontFamily: F.display, fontSize: 30, lineHeight: 1, color: C.ink }}>94</Box>
          <Box sx={{ fontSize: 8.5, color: C.bodySoft, letterSpacing: '0.06em' }}>SCORE</Box>
        </Box>
      </Box>
      <Box sx={{ flex: 1 }}>
        <Box sx={{ fontSize: 14, color: C.ink, fontWeight: 600 }}>Portfolio health</Box>
        <Box sx={{ fontSize: 11.5, color: C.success, fontWeight: 600, mt: '2px' }}>▲ +6 this month</Box>
        <Box sx={{ fontSize: 11.5, color: C.bodySoft, mt: '2px' }}>Next: book EICR at Flat 4</Box>
      </Box>
    </Box>
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', mt: '16px' }}>
      {[
        { label: 'Gas Safety', pct: 100, color: C.success },
        { label: 'EICR', pct: 88, color: C.accent },
        { label: 'EPC', pct: 96, color: C.accent },
      ].map((row) => (
        <Box key={row.label}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: 11.5, color: C.body, mb: '4px' }}>
            <span>{row.label}</span>
            <span>{row.pct}%</span>
          </Box>
          <Box sx={{ height: 6, borderRadius: '3px', background: C.border }}>
            <Box sx={{ width: `${row.pct}%`, height: 6, borderRadius: '3px', background: row.color }} />
          </Box>
        </Box>
      ))}
    </Box>
  </>
);

const bookingSteps = [
  { title: 'Expiry detected', sub: 'Gas Safety CP12 · 12 Roseberry, Flat 2', done: true },
  { title: 'Engineer matched', sub: 'Gas Safe #204xxx · 1.2 mi · £78', done: true },
  { title: 'Tenant & landlord notified', sub: 'Access confirmed · Tue 21 Jul, 10am', done: true },
];

const BookingPanel = (
  <>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
      <Box sx={{ width: 26, height: 26, borderRadius: '7px', background: C.accent, display: 'grid', placeItems: 'center', flex: 'none' }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={C.white} strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round">
          <path d="M20 6 9 17l-5-5" />
        </svg>
      </Box>
      <Box sx={{ fontSize: 10.5, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: C.accent }}>AI booking · live</Box>
    </Box>
    {bookingSteps.map((s) => (
      <Box key={s.title} sx={{ display: 'flex', gap: '11px' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Box sx={{ width: 22, height: 22, borderRadius: '20px', background: C.success, display: 'grid', placeItems: 'center', color: C.white, fontSize: 11, flex: 'none' }}>✓</Box>
          <Box sx={{ width: '2px', flex: 1, background: C.connectorLine }} />
        </Box>
        <Box sx={{ paddingBottom: '14px' }}>
          <Box sx={{ fontSize: 13, color: C.ink, fontWeight: 600 }}>{s.title}</Box>
          <Box sx={{ fontSize: 11.5, color: C.bodySoft }}>{s.sub}</Box>
        </Box>
      </Box>
    ))}
    <Box sx={{ display: 'flex', gap: '11px' }}>
      <Box sx={{ width: 22, height: 22, borderRadius: '20px', background: C.accent, display: 'grid', placeItems: 'center', color: C.white, fontSize: 11, flex: 'none' }}>→</Box>
      <Box>
        <Box sx={{ fontSize: 13, color: C.ink, fontWeight: 600 }}>Awaiting your approval</Box>
        <Box sx={{ fontSize: 11.5, color: C.accent, fontWeight: 600 }}>1 tap to confirm booking</Box>
      </Box>
    </Box>
  </>
);

const ComplianceCentrePage: React.FC = () => {
  useMarketingFonts();
  useSeoHead({
    title: 'Compliance Centre | AI Certificate Vault & Expiry Tracking — MillOS',
    description: 'Upload a Gas Safety, EPC or EICR PDF and MillOS extracts the expiry date, books the renewal and keeps every flat in your portfolio compliant automatically.',
    canonicalPath: '/compliance-centre',
  });

  return (
    <ThemeProvider theme={homepageTheme}>
    <Box className="lv-marketing" sx={{ ...homepageStyles, '--website-eyebrow-scale': 0.85, background: C.white, color: C.ink, fontFamily: F.body, fontSize: 16, lineHeight: 1.5, overflowX: 'clip' }}>
      <MarketingNav />

      <PillarHero
        eyebrow="Agent 01 · Certificates"
        title={<>A missed certificate becomes <Box component="em" sx={{ fontStyle: 'italic' }}>impossible</Box></>}
        body="MillOS reads every certificate on upload, predicts the renewal window and drives the whole workflow — engineer, tenant, evidence log — 90 days ahead."
        secondaryLabel="See the software"
        secondaryTo="/pricing"
      />

      <Box sx={{ pt: { xs: '48px', md: '96px' } }} />

      <PillarFeatureRow eyebrow="Certificate vault & expiry AI" title="Upload it once. MillOS never forgets." body="Drop in a Gas Safety, EPC or EICR PDF and MillOS extracts the expiry date, engineer ID and property, then files it and schedules the renewal window automatically." panel={VaultPanel} imageSide="left" tilt="a" />

      <PillarFeatureRow eyebrow="Live portfolio score" title="One 0–100 score for the whole portfolio." body="Every property gets a live compliance health score that updates the moment a certificate lands — and shows the single next action that will improve it most." panel={ScorePanel} imageSide="right" tilt="b" textFlex="1.1fr" />

      <PillarFeatureRow eyebrow="AI engineer booking" title="It books the engineer — you just approve." body="When a certificate nears expiry, MillOS finds a local Gas Safe or NICEIC engineer, proposes a slot, and handles all the messaging between you, the tenant and the contractor — automatically." panel={BookingPanel} imageSide="left" tilt="a" textFlex="1.1fr" />

      <Box component="section" sx={{ padding: { xs: '40px 24px', md: '40px 52px' } }}>
        <Box sx={{ maxWidth: 1100, margin: '0 auto', display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1.1fr' }, gap: { xs: '32px', md: '56px' }, alignItems: 'center' }}>
          <Box>
            <Eyebrow>Integrations</Eyebrow>
            <Box component="h2" sx={{ fontFamily: F.display, fontWeight: 400, fontSize: 'clamp(2rem,3.6vw,3rem)', lineHeight: 1.12, letterSpacing: '-0.03em', marginTop: '14px' }}>
              Works where you already work.
            </Box>
            <Box sx={{ marginTop: '16px', color: C.body, fontSize: 16, lineHeight: 1.65 }}>
              MillOS plugs into WhatsApp, Gmail and Google Calendar — so reminders, engineer messages and tenant notices go out on the channels everyone already uses, and every booking lands straight in your calendar. No new inbox to learn.
            </Box>
          </Box>
          <Box sx={{ position: 'relative', borderRadius: '20px', background: `linear-gradient(135deg,${C.tintBlue},${C.tintViolet})`, border: `1px solid ${C.glassBorder}`, boxShadow: '0 30px 70px rgba(0,0,0,0.08)', padding: '24px', overflow: 'hidden', minHeight: 300, display: 'grid', placeItems: 'center' }}>
            <Box sx={{ position: 'relative', width: 260, height: 260 }}>
              <Box sx={{ position: 'absolute', inset: 24, borderRadius: '50%', border: `1.5px dashed ${C.orbitDashInner}` }} />
              <Box sx={{ position: 'absolute', inset: 56, borderRadius: '50%', border: `1.5px dashed ${C.orbitDashOuter}` }} />
              <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 80, height: 80, borderRadius: '50%', background: C.navy, display: 'grid', placeItems: 'center', boxShadow: '0 12px 30px rgba(4,10,30,0.22)', border: `3px solid ${C.white}` }}>
                <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke={C.white} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                  <path d="m9 12 2 2 4-4" />
                </svg>
              </Box>
              <Box sx={{ position: 'absolute', top: -6, left: '50%', transform: 'translateX(-50%)', width: 58, height: 58, borderRadius: '50%', background: C.white, boxShadow: '0 10px 26px rgba(4,10,30,0.16)', display: 'grid', placeItems: 'center' }}>
                <svg width="30" height="30" viewBox="0 0 24 24">
                  <path fill="#25D366" d="M12 2a10 10 0 0 0-8.6 15.1L2 22l5-1.3A10 10 0 1 0 12 2Z" />
                  <path fill={C.white} d="M9.2 7.3c-.2-.5-.4-.5-.6-.5h-.5a1 1 0 0 0-.7.3c-.3.3-.9.8-.9 2s.9 2.3 1 2.5c.1.2 1.8 2.9 4.5 3.9 2.2.9 2.7.7 3.2.7.5-.1 1.5-.6 1.7-1.2.2-.6.2-1.1.2-1.2-.1-.1-.3-.2-.6-.4l-1.5-.7c-.2-.1-.4-.1-.5.1l-.7.9c-.1.2-.3.2-.5.1-.3-.1-1.1-.4-2-1.2-.8-.7-1.3-1.5-1.4-1.7-.1-.2 0-.4.1-.5l.4-.5.3-.5v-.4L9.2 7.3Z" />
                </svg>
              </Box>
              <Box sx={{ position: 'absolute', bottom: 14, left: 6, width: 58, height: 58, borderRadius: '50%', background: C.white, boxShadow: '0 10px 26px rgba(4,10,30,0.16)', display: 'grid', placeItems: 'center' }}>
                <svg width="30" height="24" viewBox="0 0 24 20">
                  <path fill="#4285F4" d="M2 4v12a2 2 0 0 0 2 2h1V7.5l7 5 7-5V18h1a2 2 0 0 0 2-2V4l-10 7L2 4Z" />
                  <path fill="#EA4335" d="M2 4l10 7L22 4a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z" />
                </svg>
              </Box>
              <Box sx={{ position: 'absolute', bottom: 14, right: 6, width: 58, height: 58, borderRadius: '50%', background: C.white, boxShadow: '0 10px 26px rgba(4,10,30,0.16)', display: 'grid', placeItems: 'center' }}>
                <svg width="28" height="28" viewBox="0 0 24 24">
                  <rect x="3" y="4" width="18" height="17" rx="3" fill={C.white} stroke="#4285F4" strokeWidth={1.6} />
                  <path fill="#4285F4" d="M3 7a3 3 0 0 1 3-3h12a3 3 0 0 1 3 3v1H3V7Z" />
                  <path stroke="#EA4335" strokeWidth={1.8} strokeLinecap="round" d="M8 3v3" />
                  <path stroke="#34A853" strokeWidth={1.8} strokeLinecap="round" d="M16 3v3" />
                  <text x="12" y="17.5" fontFamily="Arial" fontSize="7" fontWeight={700} fill="#4285F4" textAnchor="middle">31</text>
                </svg>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      <PillarFeatureGrid
        heading="Everything the compliance engine covers"
        iconBg={C.accent}
        icon={
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={C.white} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            <path d="m9 12 2 2 4-4" />
          </svg>
        }
        items={features}
      />

      <PillarClosingCta heading="Make the next deadline impossible to miss" />

      <MarketingFooter />
    </Box>
    </ThemeProvider>
  );
};

export default ComplianceCentrePage;
